import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Col, Row, Form as BsForm, Button } from 'react-bootstrap'
import { useCountdown } from 'usehooks-ts'

import { useNotifications } from '../../../Notifications'
import { AsyncHttpRequestState, useChange, useQueryString } from '../../../Common'
import { Form, Text, FormContextType, CheckBox, Password, Countries } from '../../../Forms'
import { LoginMsg, useAuth } from '../../../Auth'

import { Radish } from '../../components/Radish'
import { Currency, CurrencyNames, User } from '../../AppTypes'
import { API_URL, CONTENT_SOURCES } from '../../AppConfig'

import { useOrder } from '../../hooks/useOrder'
import { VoucherEdit } from '../../components/VoucherEdit'
import { OrderItem, ItemDetails } from '../../components/OrderItem'
import { View } from '../../components/View'

import payuLogo from '../../../assets/images/payu.png?format=webp'
import { Loading } from '../../components/Loading'

import MasterCard from '../../../assets/images/pay-methods/MasterCard.png?format=webp'
import Visa from '../../../assets/images/pay-methods/Visa.jpg?format=webp'
import KB from '../../../assets/images/pay-methods/KB.png?format=webp'
import CSAS from '../../../assets/images/pay-methods/CSAS.webp'
import CSOB from '../../../assets/images/pay-methods/CSOB.jpg?format=webp'
import Fio from '../../../assets/images/pay-methods/FioBanka.png?format=webp'
import { OrderResponse, PaymentType, VoucherGetResponse } from '../../../../../cz.vegilove.api/src/_api-models'

const payuLogoImg = <img width='50' src={ payuLogo.src } alt='PayU logo' />
const payPalLogoImg = <img width='50' src='https://www.paypalobjects.com/digitalassets/c/website/marketing/apac/C2/logos-buttons/optimize/26_Yellow_PayPal_Pill_Button.png' alt='PayPal logo' />


const msgs = defineMessages({
    presaleMsg: {
        description: 'App.Order.PresaleAgreement',
        defaultMessage: 'I understand and accept that I am only purchasing a pre-sale gift voucher. The seller www.VEGILOVE.EU guarantees me to activate my access to the course by 2023/03 at the latest. Or return the full value of the voucher to me.'
    }
})

export const Order = () => {

    const intl = useIntl()

    const { id } = useParams()
    const packageId: number | undefined = useMemo( () => {
        return id ? parseInt( id ) : undefined
    }, [ id ] )

    const navigate = useNavigate()

    const auth = useAuth()
    const user = auth.getUser() as User

    const voucher = useQueryString( 'voucher' )

    const formRef = useRef<HTMLFormElement | null>( null )

    const orderContext = useOrder()
    // clear order context on mount
    useEffect( () => orderContext.setRedirectUri( '' ), [ orderContext ] )

    const { addNotification, removeAllNotifications } = useNotifications()

    const [ orderResponseData, setOrderResponseData ] = useState<OrderResponse>()

    const [ countdown, { startCountdown, resetCountdown } ] = useCountdown({ countStart: 3, intervalMs: 1000 })

    const onSuccess = useCallback( ( state: AsyncHttpRequestState<unknown, OrderResponse> ) => {
        if( state.success && state.data ) {
            addNotification({ variant: 'success', category: 'app', content: intl.formatMessage({ description: 'App.Order.Success', defaultMessage: 'Your order was confirmed. Wait redirection to payment gateway.' }), timeout: 6000 })
            // if the order was for free and no payment is needed go directly to confirmation page
            if( !state.data.paymentUri ) navigate( intl.locale === 'cs-CZ' ? '/potvrzeni-objednavky' : '/order-confirmation', { state: { ...state.data } } )
            orderContext.setRedirectUri( state.data.paymentUri || '' )
            // set time to 10 seconds
            resetCountdown()
            startCountdown()
            // and save order response
            setOrderResponseData( state.data )
        } else {
            addNotification({ variant: 'danger', category: 'app', content: state.message || intl.formatMessage( { description: 'App.Order.Error', defaultMessage: 'We are sorry. Your order could not be saved. Please, try it once more or contact use via e-mail.' } ) })
        }    
    }, [] )

    const onError = useCallback( ( state: AsyncHttpRequestState<unknown, OrderResponse> ) => {
        addNotification({ variant: 'danger', category: 'app', content: state.message || intl.formatMessage( { description: 'App.Order.Error', defaultMessage: 'We are sorry. Your order could not be saved. Please, try it once more or contact use via e-mail.' } ) })
    }, [ addNotification, intl ] )

    // TODO: Define type of request! Can not use GetOrderData as orderDetails are different!
    const orderRequest = useChange<OrderResponse>({ 
        url: API_URL + '/order/new/register', 
        method: 'POST',
        label: 'order',
        onSuccess,
        onError
    })

    useEffect( () => {
        if( countdown === 0 && orderResponseData && orderResponseData?.paymentUri ) window.location.href = orderResponseData?.paymentUri
    }, [ countdown, orderResponseData ])

    const [ currency, setCurrency ] = useState<Currency>( intl.locale === 'cs-CZ' ? 'CZK' : 'EUR' )
    useEffect( () => {
        if( intl.locale === 'cs-CZ' ) setCurrency( 'CZK' )
        else if( currency === 'CZK' ) setCurrency( 'EUR' )
    }, [ intl ])

    const [ itemDetails, setItemDetails ] = useState<ItemDetails | undefined>()

    const [ discountVoucher, setDiscountVoucher ] = useState<VoucherGetResponse | undefined>()
    const calcPrice = () => {
        const discount = !itemDetails || !discountVoucher
            ? undefined
            : discountVoucher.discount <= 1 
                ? Math.floor( discountVoucher.discount * itemDetails.price )
                : discountVoucher.discount
        return itemDetails ? ( itemDetails.price - ( discount || 0 ) ) : 0
    }

    const [ paymentType, setPaymentType ] = useState<PaymentType>( 'PAYU' )

    const onFinish = useCallback( ( form: FormContextType ) => {
        if( itemDetails ){
            removeAllNotifications( 'auth ')
            const formUser = form.data
            // if we order only voucher - use partial registration
            if( itemDetails.isVoucher ) formUser.status = 'REGP'
            orderRequest.run({ data: { 
                ...formUser, 
                referrer: document.referrer,
                currency, 
                paymentType, 
                agreements: [
                    /* only if not authenticated */ !formUser ? `oou-${ intl.locale }-202301.pdf` : undefined,
                    `op-${ intl.locale }-202301.pdf`,
                    itemDetails && itemDetails.forceVoucher ? intl.formatMessage( msgs.presaleMsg ) : undefined
                ].filter( Boolean ),    
                orderDetails: [ { packageId, isVoucher: itemDetails.isVoucher, voucherCode: discountVoucher?.token } ] 
            } })
        }
    }, [ intl, currency, paymentType, itemDetails, discountVoucher, packageId, removeAllNotifications, orderRequest ] )

    return <View>
        <Loading show={ orderRequest.state.processing } />
        { !orderResponseData && <>
            <Row>
                <Col>
                    <div className='sweet-belly fs-1'><FormattedMessage description='App.Order.LetsStart' defaultMessage='Děláte dobře! Jdeme na to' />&nbsp;<Radish size={ 25 } style={{verticalAlign: 'baseline', marginBottom: '-4px', display: 'inline-block' }} /></div>
                </Col>
            </Row>
            <Row className='p-2 bg-light-brown mt-2'>
                <Col size={ 6 }>
                    <div><FormattedMessage description='App.Order.ItemsAndPrices' defaultMessage='Položky a ceny' /></div>
                </Col>
                <Col size={ 6 } className='text-end'>
                    <div style={{ maxWidth:'10em', display:'inline-block' }}><BsForm.Select
                        id='currency'
                        value={ currency }  
                        onChange={ ( ev ) => setCurrency( ev.currentTarget.value as unknown as Currency ) }
                        size="sm" 
                        aria-label={ intl.formatMessage({ description: 'App.Order.CurrencySelector', defaultMessage: 'Select currency' }) }
                    >
                        { Object.entries( CurrencyNames ).map( p => <option key={ p[ 0 ] } value={ p[ 0 ] }>{ `${ p[ 1 ] } (${ p[ 0 ] })` }</option> ) }
                    </BsForm.Select></div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Row className='border-bottom'>
                        <Col size={ 12 } md={ 3 }></Col>
                        <Col size={ 8 } md={ 6 }><FormattedMessage description='App.Order.ItemName' defaultMessage='Název' /></Col>
                        <Col size={ 4 } md={ 3 } className="text-end"><FormattedMessage description='App.Order.ItemPrice' defaultMessage='Cena' /></Col>
                    </Row>
                    { packageId && <OrderItem packageId={ packageId } currency={ currency } setItemDetails={ setItemDetails } /> }
                    { (packageId && (!itemDetails || !itemDetails.isVoucher) ) && <VoucherEdit packageId={ packageId } voucher={ voucher || '' } setDiscount={ setDiscountVoucher } /> }
                    <Row className='border-top'>
                        <Col size={ 12 } md={ 3 }></Col>
                        <Col size={ 8 } md={ 6 }><FormattedMessage description='App.Order.PriceAfterVoucher' defaultMessage='Price after voucher applied' /></Col>
                        <Col size={ 4 } md={ 3 } className="text-end">{ 
                            ( currency !== 'CZK' ? CurrencyNames[ currency ] : '' ) + calcPrice().toFixed( 2 ) + ( currency === 'CZK' ? ' Kč' : '' )
                        }</Col>
                    </Row>
                </Col>
            </Row>
            { itemDetails && calcPrice() > 0 && <>
                <Row className='p-2 bg-light-brown my-2'>
                    <Col>
                        <div><FormattedMessage description='App.Order.PaymentType' defaultMessage='Forma úhrady' /></div>
                    </Col>
                </Row>
                <Row>
                    <Col size={ 12 } md={ 3 }></Col>
                    <Col size={ 12 } md={ 9 }>
                        <BsForm.Check
                            type='radio'
                            name='paymentType'
                            id='PAYU'
                            label={ <>
                                <FormattedMessage description='App.Order.PaymentType.OnlinePayU' defaultMessage='Online payment using PayU' />
                                <br />
                                <img src={ MasterCard.src } height='30' alt='MasterCard' />
                                <img src={ Visa.src } height='30' alt='Visa' />
                                <img src={ KB.src } height='20' alt='Komerční banka a.s.' />&nbsp;
                                <img src={ CSAS } height='30' alt='Česká spořitelna a.s.' />
                                <img src={ CSOB.src } height='30' alt='ČSOB a.s.' />
                                <img src={ Fio.src } height='30' alt='Fio banka a.s.' />
                            </> }
                            checked={ paymentType === 'PAYU' }
                            onChange={ ( ev: React.ChangeEvent<HTMLInputElement> ) => { if( ev.currentTarget.checked ) setPaymentType( 'PAYU' ) } }
                        />
                        <BsForm.Check
                            type='radio'
                            name='paymentType'
                            id='BANK_TRANSFER'
                            label={ <FormattedMessage description='App.Order.PaymentType.BankTransfer' defaultMessage='Bank Transfer' /> }
                            checked={ paymentType === 'BANK_TRANSFER' }
                            onChange={ ( ev: React.ChangeEvent<HTMLInputElement> ) => { if( ev.currentTarget.checked ) setPaymentType( 'BANK_TRANSFER' ) } }
                        />
                        { /* <BsForm.Check
                            type='radio'
                            name='paymentType'
                            id='PAYPAL'
                            label={ <FormattedMessage description='App.Order.PaymentType.PayPal' defaultMessage='PayPal' /> }
                            checked={ paymentType === 'PAYPAL' }
                            onChange={ ( ev: React.ChangeEvent<HTMLInputElement> ) => { if( ev.currentTarget.checked ) setPaymentType( 'PAYPAL' ) } }
                        /> */ }
                    </Col>
                </Row>
            </> }
            <Row className='p-2 bg-light-brown my-2'>
                <Col size={ 12 } md={ 6 }>
                    <div><FormattedMessage description='App.Order.InvoiceDetails' defaultMessage='Fakturační údaje' /></div>
                </Col>
                <Col size={ 12 } md={ 6 } className='text-end'>
                    <small><FormattedMessage description='App.Order.RequiredFields' defaultMessage='Položky označené {radish} je třeba vyplnit' values={{
                        radish: <Radish size={ 16 } />
                    }} /></small>
                </Col>
            </Row>
            <Row>
                <Col className='text-center'>
                    { !auth.isAuthenticated && itemDetails && !itemDetails.isVoucher && <>
                        <FormattedMessage description='auth.or-if-registered' defaultMessage='If you are already registered - go to' />&nbsp;<Link to={ intl.locale === 'cs-CZ' ? '/prihlasit' : '/log-in' }>{ intl.formatMessage( LoginMsg )}!</Link>
                    </> }
                </Col>
            </Row>
            <Row><Col><div className='text-start p-3'>
                <Form 
                    ref={ formRef }
                    formName='register'
                    initialData={{ 
                        name: user?.name || '', 
                        email: user?.email || '', 
                        pwd: '', rePwd: '', 
                        street: user?.street || '', 
                        zip: user?.zip || '', 
                        city: user?.city || '', 
                        country: user?.country || intl.locale === 'cs-CZ' ? 'CZ' : '', 
                        status: 'REGF', 
                        GDPRagreement: user ? true : false, 
                        purchaseAgreement: false, 
                        presaleAgreement: false 
                    }}
                    onFinishCallback={ onFinish }
                >
                    <Text 
                        controlId='name' 
                        className='mb-3'
                        placeholder={ intl.formatMessage({ description: 'auth.name', defaultMessage: 'Name' }) }
                        required
                        invalidMsg={ intl.formatMessage({ description: 'auth.name-required', defaultMessage: 'Please input your name!' }) }
                    />
                    <Text 
                        controlId='email' 
                        type='email'
                        className='mb-3'
                        placeholder={ intl.formatMessage({ description: 'auth.email', defaultMessage: 'Email' }) }
                        disabled={ auth.isAuthenticated }
                        required
                        invalidMsg={ intl.formatMessage({ description: 'auth.email-required', defaultMessage: 'Please input your Email!' }) }
                    />
                    { !auth.isAuthenticated && itemDetails && !itemDetails.isVoucher && <>
                        <Password 
                            controlId='pwd' 
                            className='mb-3'
                            placeholder={ intl.formatMessage( { description: 'auth.pwd', defaultMessage: 'Password' } ) } 
                            invalidMsg={ intl.formatMessage({ description: 'auth.pwd-required', defaultMessage: 'Please input your Password!' }) }
                        />
                        <Password 
                            controlId='rePwd' 
                            className='mb-3'
                            placeholder={ intl.formatMessage( { description: 'auth.re-pwd', defaultMessage: 'Repeat Password' } ) } 
                            pattern={ ( form: FormContextType ) => (form.data[ 'pwd' ] as string).trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&') }
                            invalidMsg={ intl.formatMessage({ description: 'auth.re-pwd-required', defaultMessage: 'Please re-enter exactly your Password!' }) }
                        /> 
                    </> }
                    <Text 
                        controlId='street' 
                        className='mb-3'
                        placeholder={ intl.formatMessage( { description: 'auth.street', defaultMessage: 'Street' } ) } 
                        required
                        invalidMsg={ intl.formatMessage({ description: 'auth.street-required', defaultMessage: 'Please input street!' }) }
                    /> 
                    <Row>
                        <Col xs="4">
                            <Text 
                                controlId='zip' 
                                className='mb-3'
                                pattern={ ( form: FormContextType ) => form.data[ 'country' ] as string === 'CZ' ? '^(\\d{3}\\s{0,1}\\d{2})$' : undefined }
                                placeholder={ intl.formatMessage( { description: 'auth.zip', defaultMessage: 'ZIP' } ) } 
                                required
                                invalidMsg={ intl.formatMessage({ description: 'auth.pwd-required', defaultMessage: 'Please input your ZIP code!' }) }
                            /> 
                        </Col>
                        <Col>
                            <Text 
                                controlId='city' 
                                className='mb-3'
                                placeholder={ intl.formatMessage( { description: 'auth.city', defaultMessage: 'City' } ) } 
                                required
                                invalidMsg={ intl.formatMessage({ description: 'auth.city-required', defaultMessage: 'Please enter your city' }) }
                            /> 
                        </Col>
                    </Row>
                    <Countries 
                        apiUrl={ API_URL + '/countries' }
                        controlId='country'
                        className='mb-3'
                        placeholder={ intl.formatMessage( { description: 'auth.country', defaultMessage: 'Country' } ) } 
                        required
                        invalidMsg={ intl.formatMessage({ description: 'auth.country-required', defaultMessage: 'Please select your country' }) }
                    />
                    <Row className='mb-3 text-start'>
                        <Col>
                            { /* show only if not authenticated */ !user && <CheckBox 
                                controlId='GDPRagreement'
                                type='checkbox' 
                                required
                            >
                                <a href={ `${ CONTENT_SOURCES[ 'aws-cdn' ]}/oou-${ intl.locale }-202301.pdf` } download target='_blank' rel="noreferrer">{ intl.formatMessage({ description: 'auth.login.remember-me', defaultMessage: 'I agree with privacy policy of this web' }) }</a> 
                            </CheckBox> }
                            <CheckBox 
                                controlId='purchaseAgreement'
                                type='checkbox' 
                                required
                            >
                                <a href={ `${ CONTENT_SOURCES[ 'aws-cdn' ]}/op-${ intl.locale }-202301.pdf` } download target='_blank' rel="noreferrer">{ intl.formatMessage({ description: 'App.Order.AgreePurchaseConditions', defaultMessage: 'I agree with purchase conditions' }) }</a> 
                            </CheckBox>
                            { itemDetails && itemDetails.forceVoucher && <CheckBox
                                controlId='presaleAgreement'
                                type='checkbox' 
                                required
                            >
                                <div>{ intl.formatMessage( msgs.presaleMsg ) }</div>
                            </CheckBox> }
                        </Col>
                    </Row>
                    <Row className='mb-3'>
                        <Col>
                            <Button type='submit' className='register-form-button' disabled={ !itemDetails || orderRequest.state.processing }>
                                <FormattedMessage description='App.Order.OrderBtn' defaultMessage='I make my order' />
                            </Button>
                        </Col>
                    </Row>
                    { calcPrice() > 0 && <Row><Col>
                        { [ 'PAYU', 'PAYPAL' ].includes( paymentType ) && <FormattedMessage 
                            description='App.Order.AfterOrder' 
                            defaultMessage='After order confirmation you will be redirected to {pgwLogo} payment gateway to make your payment.' 
                            values={{ pgwLogo: paymentType === 'PAYU' ? payuLogoImg : payPalLogoImg }} 
                        /> }
                        { paymentType === 'BANK_TRANSFER' && <FormattedMessage 
                            description='App.Order.AfterOrderBankTransfer' 
                            defaultMessage='After order confirmation you will receive an e-mail with directions for your payment and attached QR code for quick bank transfer in Czech Republic.' 
                        /> }
                    </Col></Row> }
                </Form>
            </div></Col></Row>
        </>}
        { orderResponseData && <>
            <Row>
                <Col>
                    <div className='sweet-belly fs-1'><FormattedMessage description='App.Order.OrderSavedTitle' defaultMessage='Děkuji! Právě jste udělali radost sobě, mně, zvířatům i naší planetě' />&nbsp;<Radish size={ 20 } style={{verticalAlign: 'baseline', marginBottom: '-4px', display: 'inline-block' }} /></div>
                </Col>
            </Row>
            <Row><Col className='text-center'><FormattedMessage description='App.Order.OrderSavedTitle' defaultMessage='Za {seconds} sekund budete přesměrování na platbu.' values={{ seconds: countdown }} /></Col></Row>
            <Row><Col className='text-center'>
                <div><FormattedMessage description='App.Order.OrderSavedEmailInfo' defaultMessage='Rekapitulaci objednávky vám posíláme na váš e-mail.' /></div>
                <div><FormattedMessage description='App.Order.OrderSaveSignature' defaultMessage='Tým VEGILOVE' /></div>
            </Col></Row>
        </>}
    </View>
}

export default Order